// =============================================================================
// components/OrderStateActions.js — Sipariş statü geçiş butonları (Faz A)
// =============================================================================
// State machine'i (services/order-state-machine.js) UI tarafından kullanan
// kompakt buton grubu. Backend'in /next-states endpoint'i ile hangi geçişlerin
// mümkün olduğunu öğrenir, sadece geçerli butonları gösterir.
//
// Kullanım:
//   <OrderStateActions orderId={123} currentStatus="confirmed" onChange={loadOrders} />
// =============================================================================

import React, { useEffect, useState, useCallback } from "react";
import {
  Button, Stack, Chip, CircularProgress, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Typography
} from "@mui/material";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import InventoryIcon from "@mui/icons-material/Inventory";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import CancelIcon from "@mui/icons-material/Cancel";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ReplayIcon from "@mui/icons-material/Replay";
import toast from "react-hot-toast";
import { getOrderNextStates, transitionOrder } from "../services/api";

// Statü → görsel meta (renk + ikon + Türkçe etiket)
const STATUS_META = {
  pending:               { label: "Sipariş Alındı",     color: "default", icon: <InventoryIcon fontSize="small" /> },
  paid_pending_approval: { label: "Ödeme Onayı Bekliyor", color: "warning", icon: <CheckCircleIcon fontSize="small" /> },
  confirmed:             { label: "Onaylandı",          color: "info",    icon: <CheckCircleIcon fontSize="small" /> },
  preparing:             { label: "Hazırlanıyor",       color: "info",    icon: <InventoryIcon fontSize="small" /> },
  shipped:               { label: "Kargoda",            color: "primary", icon: <LocalShippingIcon fontSize="small" /> },
  delivered:             { label: "Teslim Edildi",      color: "success", icon: <DoneAllIcon fontSize="small" /> },
  cancelled:             { label: "İptal",              color: "error",   icon: <CancelIcon fontSize="small" /> },
  rejected:              { label: "Reddedildi",         color: "error",   icon: <BlockIcon fontSize="small" /> },
  failed:                { label: "Sistem Hatası",      color: "error",   icon: <BlockIcon fontSize="small" /> },
  refunded:              { label: "İade Edildi",        color: "default", icon: <ReplayIcon fontSize="small" /> }
};

export default function OrderStateActions({ orderId, currentStatus, onChange, compact = false }) {
  const [nextStates, setNextStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [transitioning, setTransitioning] = useState(false);
  const [reasonDialog, setReasonDialog] = useState({ open: false, toStatus: null });
  const [reason, setReason] = useState("");

  const loadNextStates = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getOrderNextStates(orderId);
      setNextStates(r.data.next_states || []);
    } catch (e) {
      console.error("Next states yüklenemedi:", e);
      setNextStates([]);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => { loadNextStates(); }, [loadNextStates, currentStatus]);

  // Tehlikeli geçişlerde sebep iste
  const NEEDS_REASON = ["cancelled", "rejected", "refunded"];

  const handleTransition = async (toStatus, providedReason = null) => {
    if (NEEDS_REASON.includes(toStatus) && !providedReason) {
      // Modal aç, sebep iste
      setReasonDialog({ open: true, toStatus });
      return;
    }

    if (!window.confirm(
      `Sipariş #${orderId} → ${STATUS_META[toStatus]?.label || toStatus} geçişini onaylıyor musunuz?\n\n` +
      (toStatus === "confirmed" ? "Bu işlem otomatik fatura keser ve müşteriye mail gönderir." : "") +
      (toStatus === "preparing" ? "Otomatik kargo etiketi basılır." : "") +
      (toStatus === "shipped" ? "Müşteriye 'kargonuz yola çıktı' bildirimi gider." : "") +
      (toStatus === "cancelled" ? "Bu sipariş iptal edilir, fatura iptali otomatiktir." : "")
    )) return;

    setTransitioning(true);
    try {
      const r = await transitionOrder(orderId, toStatus, providedReason);
      const sideEffectsSummary = [];
      if (r.data?.side_effects?.bh?.invoice_guid) sideEffectsSummary.push("✓ Fatura kesildi");
      if (r.data?.side_effects?.kargo?.tracking_no) sideEffectsSummary.push(`✓ Kargo: ${r.data.side_effects.kargo.tracking_no}`);
      if (r.data?.side_effects?.notify?.template) sideEffectsSummary.push("✓ Müşteriye bildirim gitti");
      toast.success(
        `${STATUS_META[r.data.from]?.label} → ${STATUS_META[r.data.to]?.label}` +
        (sideEffectsSummary.length ? `\n${sideEffectsSummary.join("\n")}` : "")
      );
      if (onChange) onChange();
      loadNextStates();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Geçiş başarısız");
    } finally {
      setTransitioning(false);
      setReasonDialog({ open: false, toStatus: null });
      setReason("");
    }
  };

  const submitReasonDialog = () => {
    if (!reason.trim()) { toast.error("Sebep zorunludur"); return; }
    handleTransition(reasonDialog.toStatus, reason.trim());
  };

  const currentMeta = STATUS_META[currentStatus] || { label: currentStatus, color: "default" };

  return (
    <Stack direction={compact ? "row" : "column"} spacing={1} alignItems={compact ? "center" : "flex-start"}>
      {/* Mevcut statü chip'i */}
      <Chip
        label={currentMeta.label}
        color={currentMeta.color}
        size="small"
        icon={currentMeta.icon}
        sx={{ fontWeight: 600 }}
      />

      {loading ? (
        <CircularProgress size={16} />
      ) : nextStates.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          Sonlandı (geçiş yok)
        </Typography>
      ) : (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
          {nextStates.map(({ status, label }) => {
            const meta = STATUS_META[status] || { label, color: "default", icon: null };
            const isDestructive = ["cancelled", "rejected"].includes(status);
            return (
              <Tooltip key={status} title={`#${orderId} → ${meta.label}`}>
                <Button
                  size="small"
                  variant={isDestructive ? "outlined" : "contained"}
                  color={meta.color === "default" ? "primary" : meta.color}
                  startIcon={meta.icon}
                  disabled={transitioning}
                  onClick={() => handleTransition(status)}
                  sx={{ minWidth: 0, px: 1.2, fontSize: 12, fontWeight: 600 }}
                >
                  {meta.label}
                </Button>
              </Tooltip>
            );
          })}
        </Stack>
      )}

      {/* Sebep dialogu (cancelled/rejected/refunded için) */}
      <Dialog open={reasonDialog.open} onClose={() => setReasonDialog({ open: false })} maxWidth="xs" fullWidth>
        <DialogTitle>
          {STATUS_META[reasonDialog.toStatus]?.label} — Sebep
        </DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            Bu işlemin sebebi audit log'a kaydedilir. Müşteriye gösterilir.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            placeholder="Örn: Müşteri talebi · Stok yok · Adres hatası"
            value={reason}
            onChange={e => setReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReasonDialog({ open: false })}>Vazgeç</Button>
          <Button variant="contained" color="error" onClick={submitReasonDialog} disabled={transitioning}>
            {transitioning ? "İşleniyor..." : "Onayla"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
